import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { CHART_TYPE_DONUT, ChartBase } from "@datanac/datanac-visualization-toolkit/dist/Components/chart/ChartBase";
import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { Box, Stack, useTheme } from "@mui/system";
import { AppContext } from "App";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useSnackbar } from "notistack";
import { formatDateLong, formatPrice } from "utility/formatters";

import "leaflet/dist/leaflet.css";

import { ExpandMore } from "@mui/icons-material";
import { UsersApiHelper } from "api/ApiHelper";
import { BUDGET_STATUS_ACTIVE } from "components/Budget/helpers";
import _ from "lodash";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";

import { ReportsIcon } from "../Icons/Icons";
import {
  REVENUE_LABEL_MISCELLANEOUS_PROGRAM_PAYMENTS,
  REVENUE_LABEL_OTHER_GOVERNMENT_PAYMENTS,
} from "../ArcPlc/FarmPaymentConstants";
import useInsurance from "../Insurance/useInsurance";
import useInsuranceProjectedPayment from "../Insurance/useInsuranceProjectedPayment";
import useRevenueReport, { REVENUE_LABEL_ARC_PLC_PAYMENTS } from "../Marketing/useRevenueReport";
import useYieldForecastAggregate from "../Weather/useYieldForecastAggregate";
import { useMarketYearAveragePrices } from "api-toolkit/useMarketYearAveragePrices";
import { CropOverview } from "./CropOverview";
import CropProgressBars from "./CropProgressBars";
import { DashboardStatsCards } from "./DashboardStatsCards";
import { DashBoardTableCard } from "./DashBoardTableCard";
import ProgressBar from "./ProgressBar";
import SkeletonCropTable from "./SkeletonCropTable";

export const RevenueDashboardWorkspace = () => {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { producer_token, crop_year } = globalState || {};

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const currentDate = formatDateLong(new Date());
  const [selectedCrop, setSelectedCrop] = useState(() => {
    const storedCrop = localStorage.getItem("selectedCrop");
    return storedCrop || "All";
  });

  // Component state
  const [currentYear, setCurrentYear] = useState(crop_year);
  const [budgetTotal, setBudgetTotal] = useState(0);
  const formMaxWidth = "3000px";

  // Common error handler (stable for hooks that list onError in deps)
  const handleError = useCallback(
    (err) => {
      console.warn(err);
      enqueueSnackbar(err?.detail || err?.message || JSON.stringify(err), {
        preventDuplicate: true,
        variant: "error",
      });
    },
    [enqueueSnackbar]
  );

  const handleCropSelection = (cropName) => {
    setSelectedCrop(cropName);
    if (cropName === "All") {
      localStorage.removeItem("selectedCrop");
    } else {
      localStorage.setItem("selectedCrop", cropName);
    }
  };

  // Internal function for data access
  const loadBudgetData_Intern = async (year) => {
    if (!producer_token || !year) return null;

    try {
      const budgets = await UsersApiHelper.selectBudgets(
        {
          year,
          is_active: true,
          status: BUDGET_STATUS_ACTIVE,
        },
        globalState
      );

      if (budgets?.length) {
        const budgetCountyPractices = await UsersApiHelper.selectBudgetCountyPractices(
          {
            budget_id: budgets[0].id,
            is_active: true,
          },
          globalState
        );

        const total = _.sum(budgetCountyPractices.map((cp) => cp.production_cost));
        return { budgets, budgetCountyPractices, total };
      }
      return null;
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  // State management function
  const loadBudgetData = async () => {
    const result = await loadBudgetData_Intern(currentYear);
    if (result) {
      setBudgetTotal(result.total);
    }
  };

  // Load budget data when year changes
  useEffect(() => {
    loadBudgetData(currentYear);
  }, [currentYear, producer_token]);

  const {
    isLoadingData,
    revenueDetail,
    revenueSummary,
    calculateFuturesOptionsHedgedTotal,
    farmPlantingData,
    getTransactionHistoryTotal_Revenue,
    farmPaymentTotals,
    isLoadingFarmPayments,
  } = useRevenueReport({
    onError: handleError,
    currentYear,
    producerState: producer_token ? { ...globalState, producer_token } : null,
  });

  const { budgetCountyPractices } = useInsurance({
    crop_year: currentYear,
    skipStressTest: true,
    skipInsurancePlans: true,
  });

  const yieldAgg = useYieldForecastAggregate({
    budgetCountyPractices,
    crop_year: currentYear,
  });

  const dashboardCommodities = useMemo(
    () => _.uniq((budgetCountyPractices || []).map((b) => b.commodity_name).filter(Boolean)),
    [budgetCountyPractices]
  );

  const typeNameByCommodity = useMemo(() => {
    const map = {};
    (budgetCountyPractices || []).forEach((bcp) => {
      if (bcp.commodity_name && bcp.type_name && !map[bcp.commodity_name]) {
        map[bcp.commodity_name] = bcp.type_name;
      }
    });
    return map;
  }, [budgetCountyPractices]);

  const myaPrices = useMarketYearAveragePrices({
    commodities: dashboardCommodities,
    crop_year: currentYear,
    typeNameByCommodity,
  });

  const projectedPayment = useInsuranceProjectedPayment({
    budgetCountyPractices,
    yieldAgg,
    myaPrices,
    crop_year: currentYear,
  });

  const cropRevenue = useMemo(() => {
    if (!revenueDetail?.crops) return [];
    return revenueDetail.crops.map((crop) => ({
      name: crop.name,
      value: crop.revenueData?.find((revenue) => /Total Revenue/i.test(revenue.label))?.value || 0,
    }));
  }, [revenueDetail?.crops]);

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const currentCrop = useMemo(() => {
    if (!selectedCrop || !revenueSummary.crops) return null;
    return revenueSummary.crops.find((crop) => crop.name === selectedCrop);
  }, [revenueSummary.crops, selectedCrop]);

  const totalRevenueSummary = useMemo(
    () => [
      {
        label: "Forward Sales",
        value: formatPrice(
          revenueDetail?.crops?.reduce((total, crop) => {
            const soldRevenue = crop.revenueData?.find((revenue) => revenue.label === "Sold Revenue")?.value || 0;
            return total + soldRevenue;
          }, 0),
          0
        ),
      },
      {
        label: "Unsold Revenue",
        value: formatPrice(
          revenueDetail?.crops?.reduce((total, crop) => {
            const unsoldRevenue = crop.revenueData?.find((revenue) => revenue.label === "Unsold Revenue")?.value || 0;
            return total + unsoldRevenue;
          }, 0),
          0
        ),
      },
      {
        label: "Net Hedging Gain/Loss",
        value: formatPrice(
          revenueDetail?.crops?.reduce((total, crop) => {
            const closedGainLoss =
              crop.revenueData?.find((revenue) => revenue.label === "Closed Gain/Loss")?.value || 0;
            const openGainLoss = crop.revenueData?.find((revenue) => revenue.label === "Open Gain/Loss")?.value || 0;
            return total + closedGainLoss + openGainLoss;
          }, 0),
          0
        ),
      },
      {
        label: "Other Revenue",
        value: formatPrice(
          revenueDetail?.crops?.reduce((total, crop) => {
            const otherPayments = crop.revenueData?.find((revenue) => revenue.label === "Other Payments")?.value || 0;
            return total + otherPayments;
          }, 0),
          0
        ),
      },
      {
        label: "Crop Insurance Guarantee",
        value: formatPrice(
          revenueDetail?.crops?.reduce((total, crop) => {
            const insuranceGuarantee =
              crop.insuranceData?.find((insurance) => insurance.label === "Crop Insurance Guarantee")?.value || 0;
            return total + insuranceGuarantee;
          }, 0),
          0
        ),
        highlighted: true,
      },
      {
        label: REVENUE_LABEL_ARC_PLC_PAYMENTS,
        value: formatPrice(
          revenueDetail?.crops?.reduce((total, crop) => {
            const arcPlcPayments =
              crop.revenueData?.find((revenue) => revenue.label === REVENUE_LABEL_ARC_PLC_PAYMENTS)?.value || 0;
            return total + arcPlcPayments;
          }, 0),
          0
        ),
        highlighted: true,
      },
      {
        label: "Insurance Payment (projected)",
        value: projectedPayment.isLoading
          ? "--"
          : formatPrice(projectedPayment.farmTotal?.totalIndemnity, 0),
        tooltipText: projectedPayment.isLoading
          ? undefined
          : Object.entries(projectedPayment.byCommodity || {})
              .flatMap(([crop, agg]) => [
                `${crop} ${formatPrice(agg.totalIndemnity, 0)}`,
                ...agg.breakdown.map(
                  (c) =>
                    `${c.county_name} (${c.practice_name}) ${formatPrice(c.projectedIndemnity, 0)}`
                ),
              ])
              .join("\n") || undefined,
        highlighted: true,
      },
      {
        label: REVENUE_LABEL_OTHER_GOVERNMENT_PAYMENTS,
        value: isLoadingFarmPayments ? "--" : formatPrice(farmPaymentTotals.otherGovernment, 0),
        highlighted: true,
      },
      {
        label: REVENUE_LABEL_MISCELLANEOUS_PROGRAM_PAYMENTS,
        value: isLoadingFarmPayments ? "--" : formatPrice(farmPaymentTotals.miscellaneous, 0),
        highlighted: true,
      },
    ],
    [revenueDetail?.crops, projectedPayment, isLoadingFarmPayments, farmPaymentTotals]
  );

  const chartDataSalesTypeTotal = [
    {
      category: "Cash",
      id: 1,
      dataValueY_mean: (revenueDetail?.crops || []).reduce((total, crop) => {
        const cashAmount =
          crop.revenueData?.find((revenue) => revenue.label === "Sold Revenue")?.detailedRevenue?.amount_cash || 0;
        return total + cashAmount;
      }, 0),
      stack: 1,
    },
    {
      category: "HTA",
      id: 2,
      dataValueY_mean: (revenueDetail?.crops || []).reduce((total, crop) => {
        const htaAmount =
          crop.revenueData?.find((revenue) => revenue.label === "Sold Revenue")?.detailedRevenue?.amount_hta || 0;
        return total + htaAmount;
      }, 0),
      stack: 1,
    },
    {
      category: "Futures & Options",
      id: 3,
      dataValueY_mean: (revenueDetail?.crops || []).reduce((total, crop) => {
        return total + (getTransactionHistoryTotal_Revenue(crop.name) || 0);
      }, 0),
      stack: 1,
    },
    {
      category: "Basis",
      id: 4,
      dataValueY_mean: (revenueDetail?.crops || []).reduce((total, crop) => {
        const basisAmount =
          crop.revenueData?.find((revenue) => revenue.label === "Sold Revenue")?.detailedRevenue?.amount_basis || 0;
        return total + basisAmount;
      }, 0),
    },
    {
      category: "Unsold",
      id: 5,
      dataValueY_mean: (revenueDetail?.crops || []).reduce((total, crop) => {
        const unsoldRevenue = crop.revenueData?.find((revenue) => revenue.label === "Unsold Revenue")?.value || 0;
        return total + unsoldRevenue;
      }, 0),
    },
  ];

  const hasChartData = chartDataSalesTypeTotal.some((d) => d.dataValueY_mean > 0);

  const calculateCropsProtectedPercent = useCallback(() => {
    if (!revenueDetail?.crops?.length) return 0;

    const totals = revenueDetail.crops.reduce(
      (acc, crop) => {
        // "Total Hedged Position" (unit_quantity_hedge_total) already includes
        // cash/HTA forward sales, so adding "Sold" would double-count them.
        const hedged = crop.yieldData?.find((item) => item.label === "Total Hedged Position")?.value || 0;
        const production = crop.yieldData?.find((item) => item.label === "Projected Production")?.value || 0;

        return {
          totalProtected: (acc.totalProtected || 0) + hedged,
          totalProduction: (acc.totalProduction || 0) + production,
        };
      },
      { totalProtected: 0, totalProduction: 0 }
    );

    return totals.totalProduction > 0 ? totals.totalProtected / totals.totalProduction : 0;
  }, [revenueDetail]);

  return (
    <Box className="revenue-dashboard-workspace">
      <WorkspaceHeader
        title="Revenue Dashboard"
        subtitle={revenueDetail?.farmerInfo?.name ? revenueDetail.farmerInfo.name : <>&nbsp;</>}
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />
      <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }} className="only-print">
        {`as of ${currentDate}`}
      </Typography>

      <Box className="no-print">
        <WorkspacePageHeader
          title="Farm Overview"
          currentYear={currentYear}
          onYearChange={setCurrentYear}
          isLoading={isLoadingData}
        />
      </Box>

      {/* New Modern Dashboard Cards */}
      <Box
        sx={{
          mt: 1,
          width: "100%",
          maxWidth: formMaxWidth,
          margin: "0 auto",
        }}
      >
        <DashboardStatsCards
          revenueDetail={revenueDetail}
          budgetTotal={budgetTotal}
          farmPlantingData={farmPlantingData}
          isCropSpecific={false}
        />
        {/* Wrap the summary boxes with responsive flex layout */}
        <Grid
          container
          spacing={2}
          alignItems="flex-start"
          sx={{
            width: "100%",
            "@media print": {
              paddingTop: "20px",
            },
          }}
        >
          {/* Total revenue summary */}
          <Grid
            item
            xs={12}
            lg={6}
            xl={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: ".75rem",
              // flex: 1,
              boxSizing: "border-box",
              "@media print": {
                flexBasis: "50%",
                maxWidth: "50%",
                width: "50%",
                order: 1,
                margin: 0,
                padding: "0 10px",
                boxSizing: "border-box",
              },
            }}
          >
            <Typography
              sx={{
                color: "var(--mui-palette-primary-dark)",
                fontSize: "1.5rem",
                fontWeight: "700",
                "@media print": {
                  fontSize: "0.85rem !important",
                  lineHeight: "1.2",
                  marginBottom: "0.5rem",
                },
              }}
              className="revenue-dashboard-card"
            >
              Total revenue summary
            </Typography>
            <DashBoardTableCard data={totalRevenueSummary} />
          </Grid>

          {/* % of crops sold and hedged - separate on mobile */}
          <Grid
            item
            xs={12}
            lg={6}
            xl={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: ".75rem",
              boxSizing: "border-box",
              "@media print": {
                width: "100%",
                order: 3,
                margin: 0,
                padding: "0 10px",
                boxSizing: "border-box",
              },
            }}
          >
            <Typography
              className="revenue-title"
              sx={{
                color: "var(--mui-palette-primary-dark)",
                fontSize: "1.5rem",
                fontWeight: "700",
                "@media print": {
                  fontSize: "0.85rem !important",
                  lineHeight: "1.2",
                  marginBottom: "0.5rem",
                },
              }}
            >
              % of crops sold and hedged
            </Typography>
            <Card
              sx={{
                boxShadow: "none",
                borderRadius: "0.875rem",
                marginBottom: 2,
              }}
            >
              <CardContent>
                <ProgressBar title="All crops" value={Math.round(calculateCropsProtectedPercent() * 100)} />
              </CardContent>
            </Card>

            {/* Revenue by crop - shown below on mobile, beside on larger screens */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                gap: ".75rem",
                justifyContent: "flex-end",
              }}
              className="revenue-dashboard-card"
            >
              <Typography
                className="revenue-title"
                sx={{
                  color: "var(--mui-palette-primary-dark)",
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  "@media print": {
                    fontSize: "0.85rem !important",
                    lineHeight: "1.2",
                    marginBottom: "0.5rem",
                  },
                }}
              >
                Revenue by crop
              </Typography>
              <Card sx={{ boxShadow: "none", borderRadius: "14px" }}>
                <CardContent>
                  <CropProgressBars crops={cropRevenue} />
                </CardContent>
              </Card>
            </Box>
          </Grid>

          {/* % of sales type */}
          <Grid
            item
            xs={12}
            lg={12}
            xl={4}
            sx={{
              // pt: 1,
              display: "flex",
              flexDirection: "column",
              gap: ".75rem",
              // flex: { xs: "none", md: 1 },
              boxSizing: "border-box",
              "@media print": {
                flexBasis: "50%",
                maxWidth: "50%",
                width: "50%",
                order: 2,
                margin: 0,
                padding: "0 10px",
                boxSizing: "border-box",
              },
            }}
          >
            <Typography
              sx={{
                color: "var(--mui-palette-primary-dark)",
                fontSize: "1.5rem",
                fontWeight: "700",
                "@media print": {
                  fontSize: "0.85rem !important",
                  lineHeight: "1.2",
                  marginBottom: "0.5rem",
                },
              }}
              className="revenue-dashboard-card"
            >
              % of sales type
            </Typography>
            <Card sx={{ boxShadow: "none", borderRadius: "14px", flex: 1, display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  {hasChartData ? (
                    <ChartBase
                      data={chartDataSalesTypeTotal}
                      type={CHART_TYPE_DONUT}
                      size="small"
                      allowedChartTypes={[CHART_TYPE_DONUT]}
                      isShowChartSelection={false}
                      width={320}
                      height={300}
                      showLegend={true}
                      scaleColors={[
                        theme.palette.colors.textVibrantGreen, // Green $$ (vibrant)
                        theme.palette.colors.textDarkGreen, // Green $ (slightly darker)
                        theme.palette.colors.textGraySlate, // Gray
                        theme.palette.colors.textWarningYellow, // Yellow
                        theme.palette.colors.textOrangeAccent, // Orange
                        theme.palette.colors.textDangerRed, // Red
                      ]}
                      legendDirection={"vertical"}
                      legendFontSize={16}
                      renderer="svg"
                    />
                  ) : (
                    <Typography variant="body1" color="textSecondary">
                      No data available
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          "@media print": {
            display: "block",
            pageBreakAfter: "always",
            pageBreakInside: "avoid",
          },
        }}
      >
        <Divider
          sx={{
            my: 2,
            "@media print": {
              display: "none",
            },
          }}
        />
      </Box>

      <Box sx={{ mt: 0, mb: 4, width: "100%", maxWidth: formMaxWidth, margin: "0 auto" }}>
        {revenueSummary.crops && (
          <Stack
            direction="row"
            sx={{
              "@media print": {
                display: "none",
              },
            }}
            spacing={2}
            alignItems="center"
            className="headerWithMenuButton"
          >
            <Typography variant="h1">Crop Overview</Typography>

            <PopupState variant="popover" popupId="crop-view-menu">
              {(popupState) => (
                <>
                  <Button variant="outlined" {...bindTrigger(popupState)} endIcon={<ExpandMore />}>
                    {selectedCrop || "Select Crop"}
                  </Button>

                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      key="all"
                      onClick={() => {
                        handleCropSelection("All");
                        popupState.close();
                      }}
                    >
                      All
                    </MenuItem>
                    {revenueSummary.crops?.map((crop, index) => (
                      <MenuItem
                        key={index}
                        onClick={() => {
                          handleCropSelection(crop.name);
                          popupState.close();
                        }}
                      >
                        {crop.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </PopupState>
          </Stack>
        )}

        {/* Crop Summary Tables */}
        {revenueSummary.crops && (
          <Grid container justifyContent="flex-start">
            {isLoadingData ? (
              <SkeletonCropTable />
            ) : revenueSummary.crops?.length ? (
              selectedCrop === "All" ? (
                revenueSummary.crops.map((crop) => (
                  <Fragment key={crop.name}>
                    <Typography
                      component="span"
                      variant="h1"
                      sx={{
                        color: "var(--mui-palette-secondary-dark)",
                        "@media print": {
                          pageBreakBefore: "always",
                          pageBreakAfter: "avoid",
                          breakAfter: "avoid",
                          display: "block",
                          marginBottom: "8px",
                        },
                        "@media print and (orientation: landscape)": {
                          pageBreakAfter: "avoid",
                          breakAfter: "avoid",
                        },
                      }}
                    >
                      {crop.name || ""}
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        "@media print": {
                          breakInside: "avoid",
                          marginBottom: "20px",
                        },
                      }}
                    >
                      <CropOverview
                        revenueDetail={revenueDetail}
                        currentCrop={crop}
                        calculateFuturesOptionsHedgedTotal={calculateFuturesOptionsHedgedTotal}
                        yieldAgg={yieldAgg}
                        projectedPayment={projectedPayment}
                        isLoadingFarmPayments={isLoadingFarmPayments}
                      />
                    </Box>
                  </Fragment>
                ))
              ) : (
                currentCrop && (
                  <Box
                    sx={{
                      width: "100%",
                      "@media print": {
                        pageBreakBefore: "always",
                        breakBefore: "page",
                        pageBreakAfter: "avoid",
                        breakAfter: "avoid",
                        pageBreakInside: "avoid",
                        breakInside: "avoid",
                        marginBottom: "20px",
                      },
                    }}
                  >
                    <CropOverview
                      revenueDetail={revenueDetail}
                      currentCrop={currentCrop}
                      calculateFuturesOptionsHedgedTotal={calculateFuturesOptionsHedgedTotal}
                      yieldAgg={yieldAgg}
                      projectedPayment={projectedPayment}
                      isLoadingFarmPayments={isLoadingFarmPayments}
                    />
                  </Box>
                )
              )
            ) : (
              <Grid item xs={12} md={10}>
                <Table size="small" sx={{ mt: 4, width: "100%" }}>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body1">No crop data available</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Divider />
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </Box>
  );
};
